import React, { useState, useContext, useEffect } from 'react';
import { SessionContext } from './SessionContext.js';
import NslMailAPI from '../NslMailAPI.js';

const Contact = () => {
  const session = useContext(SessionContext);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email_address: '',
    comment: '',
    website: ''   // honeypot -- see the hidden field in the form below
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [powMessage, setPowMessage] = useState('');

  useEffect(() => {
    if (session.isLoggedIn && session.user) {
      setFormData(prev => ({
        ...prev,
        first_name: session.user.first_name || '',
        last_name: session.user.last_name || '',
        email_address: session.user.primary_email_address || ''
      }));
    }
  }, [session.isLoggedIn, session.user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      const api = new NslMailAPI(session.accessToken);
      await api.submitContactForm(formData, setPowMessage);

      setSubmitted(true);
      setFormData({
        first_name: '',
        last_name: '',
        email_address: '',
        comment: '',
        website: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setError(error.message || 'An error occurred while submitting the form');
    } finally {
      setIsSubmitting(false);
      setPowMessage('');
    }
  };

  if (submitted) {
    return (
      <div className="container-md mt-8">
        <div className="card">
          <div className="card-header">
            <h1 className="text-2xl font-semibold text-gray-900">Contact Form</h1>
          </div>
          <div className="card-content text-center">
            <div className="bg-green-50 p-6 rounded-lg mb-6">
              <h2 className="text-lg font-medium text-success-500 mb-2">Thank You!</h2>
              <p className="text-gray-600">Your message has been submitted successfully. We'll get back to you as soon as possible.</p>
            </div>
            <button 
              onClick={() => setSubmitted(false)} 
              className="btn btn-secondary"
            >
              Submit Another Message
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-md mt-8">
      <div className="card">
        <div className="card-header">
          <h1 className="text-2xl font-semibold text-gray-900">Contact Form</h1>
          <p className="text-gray-600 mt-2">Get in touch with us - we'd love to hear from you!</p>
        </div>
        <div className="card-content">
          {error && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-6">
              <p className="text-error-500">{error}</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="form-group">
                <label htmlFor="first_name" className="form-label">
                  First Name
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="last_name" className="form-label">
                  Last Name
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="email_address" className="form-label">
                Email Address
              </label>
              <input
                type="email"
                id="email_address"
                name="email_address"
                value={formData.email_address}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="comment" className="form-label">
                Your Message
              </label>
              <textarea
                id="comment"
                name="comment"
                value={formData.comment}
                onChange={handleChange}
                rows="6"
                placeholder="Tell us how we can help you..."
                className="form-input"
                style={{ resize: 'vertical', minHeight: '120px' }}
                required
              />
            </div>
            
            {/* Honeypot. Hidden from people, irresistible to form-filling
                bots; the server silently discards anything that fills it. */}
            <div className="form-trap" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                tabIndex="-1"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore=""
              />
            </div>

            <div className="flex justify-end items-center gap-6">
              {powMessage && (
                <span className="form-help">{powMessage}</span>
              )}
              <button
                type="submit"
                className="btn btn-primary btn-lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Send Message'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;